import { API, graphqlOperation } from 'aws-amplify';
import moment from 'moment';
import * as _ from 'lodash';
import {
  createTask,
  deleteTask,
  updateTask,
  rpcService,
  createTaskType,
  updateTaskType,
  createTeam,
  updateTeam,
  createAsset,
  updateAsset,
  updateUser,
  joynEngine,
  updateSettings,
  createComment,
  deleteRole,
  updateRole,
  createRole,
  updateComment,
  deleteFactor,
  updateFactor,
  createFactor,
} from '../graphql/mutations';
import {
  getAssetsByFieldAndTypes,
  getAssetByAreasAndTypes,
  getUsersByTeams,
  getUsersByRoles,
  getUserByIds,
  getFields,
  getUserByUserName,
  getAssetsByTeamsAndTypes,
  getAreasByTeams,
  getAssetsByStopAndTypes,
  getAsset,
  getTeamsByTeamIds,
  getTasksByField,
  getTasksByUsers,
  getTaskById,
  searchAssets,
  getCommentsByTaskId,
  searchTaskTypes,
  searchTeams,
  searchUsers,
  getSettings,
  searchEntityAttributes,
  getTasksByFieldPaginated,
  getTasksByAreasPaginated,
  getRouteEngineHistoryByField,
  searchPicklists,
  getTaskType,
  getUserByAreas,
  getRoles,
  getFactorsByType,
  getFactorById,
} from '../graphql/queries';

import {
  onUpdateRequestId,
  onChangeFieldTask,
  // onUpdateUser,
  onBulkUserChange,
  onCreateComment,
  onUpdateComment,
  onDeleteComment,
  onUserLocationUpdate,
} from '../graphql/subscriptions';
import AuthUtilities from '../HelperMethods/AuthUtilities';
import { zlibDeflate } from '../HelperMethods/UtilityFunctions';
import BrotliDecodeFromBase64String from '../HelperMethods/BrotliDecodeFromBase64String';
import logUtils from '../HelperMethods/LogUtils';
import awsmobile from '../aws-exports';

const INTEGRATION_API_NAME = awsmobile.API.endpoints[0].name;
const FDG_API_NAME = awsmobile.API.endpoints[1]?.name;
const STATUS_CODE_401_ERROR_MESSAGE = 'Request failed with status code 401';

/**
 * This function makes the api request as per graphqlOperation arg
 * In case of idToken has been expired, it requests idToken from cognito using refresh token and make the request again
 */
const promiseWrapperAPI = (graphqlOp, count = 0) => {
  return new Promise((resolve, reject) => {
    AuthUtilities.checkExpiryTime().then(() => {
      API.graphql(graphqlOp)
        .then((response) => {
          resolve(response);
        })
        .catch((e1) => {
          if (e1 && e1.errors && e1.errors.length > 0 && e1.errors[0].message === STATUS_CODE_401_ERROR_MESSAGE) {
            logUtils.logError(new Error('401 code received from api. expiry time logic not working'));
            // In case, status code is 401
            if (count < 3) {
              AuthUtilities.refreshToken() // refreshing token
                .then(() => {
                  count += 1;
                  promiseWrapperAPI(graphqlOp, count) // making the api request again
                    .then((response) => {
                      resolve(response);
                    })
                    .catch((e3) => {
                      reject(e3);
                    });
                })
                .catch((e2) => {
                  console.error(e2);
                  AuthUtilities.logoutUser();
                  reject(e2);
                });
            } else {
              AuthUtilities.logoutUser();
            }
          } else {
            reject(e1);
          }
        });
    });
  });
  // in case it is observable
  /* const originalObservable = promiseOrObservable;
  return new Observable((observer) => {
    originalObservable.subscribe({
      next: (response) => {
        observer.next(response);
      },
      error: (e1) => {
        if (e1 && e1.errors && e1.errors.length > 0 && e1.errors[0].message === STATUS_CODE_401_ERROR_MESSAGE) {
          // in case of status code 401
          AuthUtilities.refreshToken() // refreshing token
            .then(() => {
              count += 1;
              promiseWrapperAPI(graphqlOp, count).subscribe({
                // making api request again
                next: (res) => {
                  observer.next(res);
                },
                error: (e3) => {
                  observer.error(e3);
                },
                complete: () => {
                  observer.complete();
                },
              });
            })
            .catch((e2) => {
              console.error(e2);
              AuthUtilities.logoutUser();
              observer.error(e2);
            });
        } else {
          observer.error(e1);
        }
      },
      complete: () => {
        observer.complete();
      },
    });
  }); */
};

class ApiService {
  static getAllTasks(fieldId) {
    return promiseWrapperAPI(
      graphqlOperation(getTasksByField, {
        fieldId,
      })
    );
  }

  static getUsersByRoles(roles) {
    return promiseWrapperAPI(graphqlOperation(getUsersByRoles, { roles }));
  }

  static getUsersByTeams(teams) {
    return promiseWrapperAPI(graphqlOperation(getUsersByTeams, { teams }));
  }

  static getTeamsByTeamIDs(teamIDs) {
    return promiseWrapperAPI(
      graphqlOperation(getTeamsByTeamIds, {
        teamIds: teamIDs,
      })
    );
  }

  static createTask(task) {
    return promiseWrapperAPI(graphqlOperation(createTask, { input: task }));
  }

  static createTeam(teamInput) {
    return promiseWrapperAPI(graphqlOperation(createTeam, { input: teamInput }));
  }

  static updateTeam(updateTeamInput) {
    return promiseWrapperAPI(graphqlOperation(updateTeam, { input: updateTeamInput }));
  }

  static createAsset(createAssetInput) {
    return promiseWrapperAPI(graphqlOperation(createAsset, { input: createAssetInput }));
  }

  static updateAsset(updateAssetInput) {
    return promiseWrapperAPI(graphqlOperation(updateAsset, { input: updateAssetInput }));
  }

  static updateUser(updateUserInput) {
    return promiseWrapperAPI(graphqlOperation(updateUser, { input: updateUserInput }));
  }

  static deleteTask(id) {
    return promiseWrapperAPI(graphqlOperation(deleteTask, { input: id }));
  }

  static updateTask(task) {
    return promiseWrapperAPI(graphqlOperation(updateTask, { input: task }));
  }

  static runEngine(
    requestId,
    fieldId,
    areaIds,
    tenantId,
    date,
    userId,
    engineSettings,
    planningStartDate,
    planningEndDate,
    tasks,
    currentPlanningStartTime,
    currentPlanningEndTime,
    startTimeDateLte,
    autoFreezTimeDateLte
  ) {
    const input = {
      ...engineSettings,
      fieldId,
      areaIds,
      userId,
      date,
      requestId,
      planningStartDate,
      planningEndDate,
      tasks,
      currentPlanningStartTime,
      currentPlanningEndTime,
      startTimeDateLte,
      autoFreezTimeDateLte,
    };
    return promiseWrapperAPI(graphqlOperation(joynEngine, { input: JSON.stringify(input) }));
  }

  static rerunEngine(requestId, pathToFile, searchOptions) {
    const input = {
      method: 'rerunRoutingEngine',
      data: JSON.stringify({ search_options: searchOptions, pathToFile, requestId }),
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static publishTasks(tasks, username, ispublishing, publishAsPlanned, dates) {
    const input = {
      method: 'publish',
      data: JSON.stringify({ tasks, username, ispublishing, publishAsPlanned, dates }),
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static updateUsers(users) {
    const input = {
      method: 'updateUser',
      data: JSON.stringify(users),
    };

    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static getTaskHistory(data) {
    const input = {
      method: 'getTaskHistory',
      data: JSON.stringify(data),
    };
    return this.rpcServiceMethod(input);
  }

  static getDistanceMatrix(fieldId, stopIds) {
    const input = {
      method: 'getDistanceMatrix',
      data: JSON.stringify({ fieldId, stopIds }),
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static getStopIdsPreviewForCriteria(criteria) {
    const input = {
      method: 'getstopsByAreaCriteria',
      data: JSON.stringify(criteria),
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static bulkDelete(tasks) {
    const input = {
      method: 'bulkDelete',
      data: JSON.stringify(tasks),
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static bulkUnAssign(taskIds) {
    const input = {
      method: 'bulkUnAssign',
      data: JSON.stringify({ taskIds }),
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static createTaskType(taskType) {
    return promiseWrapperAPI(graphqlOperation(createTaskType, { input: taskType }));
  }

  static updateTaskType(taskType) {
    return promiseWrapperAPI(graphqlOperation(updateTaskType, { input: taskType }));
  }

  // area
  static getAreasByTeams(teamIDs) {
    return promiseWrapperAPI(
      graphqlOperation(getAreasByTeams, {
        teams: teamIDs,
      })
    );
  }

  // this will fetch first 1000 assets
  static getAssetsByFieldAndTypes(fieldID, types) {
    return promiseWrapperAPI(
      graphqlOperation(getAssetsByFieldAndTypes, {
        fieldId: fieldID,
        types,
      })
    );
  }

  // this will fetch all the assets..though the limit is 10000
  static getAssetsByFieldAndTypesRPC(fieldID, types) {
    const input = {
      method: 'getAssetsByFieldAndTypes',
      data: JSON.stringify({ fieldId: fieldID, types }),
    };
    return this.rpcServiceMethod(input);
  }

  // this will fetch first 1000 assets
  static getAssetByAreasAndTypes(areaIDs, types) {
    return promiseWrapperAPI(
      graphqlOperation(getAssetByAreasAndTypes, {
        areas: areaIDs,
        types,
      })
    );
  }

  // this will fetch all the assets..though the limit is 10000
  static getAssetsByAreasAndTypesRPC(areaIDs, types) {
    const input = {
      method: 'getAssetsByAreasAndTypes',
      data: JSON.stringify({ areas: areaIDs, types }),
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static getAssetByTeamsAndTypes(teamIDs, types) {
    return promiseWrapperAPI(
      graphqlOperation(getAssetsByTeamsAndTypes, {
        teams: teamIDs,
        types,
      })
    );
  }

  static getAssetsByStopAndTypes(stopID, types) {
    return promiseWrapperAPI(
      graphqlOperation(getAssetsByStopAndTypes, {
        stopId: stopID,
        types,
      })
    );
  }

  // static joynRpcService(action, params) {
  //     return (graphqlOperation(joynRpcService, { input: { action: action, params: JSON.stringify(params) } }))
  // }

  static getUsersByIDs(ids) {
    return promiseWrapperAPI(
      graphqlOperation(getUserByIds, {
        userIds: ids,
      })
    );
  }

  static getAllFields() {
    return promiseWrapperAPI(graphqlOperation(getFields));
  }

  static getUserByUserName(userName) {
    return promiseWrapperAPI(
      graphqlOperation(getUserByUserName, {
        userName,
      })
    );
  }

  static getAsset(assetId) {
    return promiseWrapperAPI(
      graphqlOperation(getAsset, {
        id: assetId,
      })
    );
  }

  static deleteItem({ id, graphqlOperationName }) {
    return promiseWrapperAPI(graphqlOperation(graphqlOperationName, { input: { id } }));
  }

  static getTasksByUserIds(userIds) {
    return promiseWrapperAPI(
      graphqlOperation(getTasksByUsers, {
        users: userIds,
      })
    );
  }

  static getTask(taskId) {
    return promiseWrapperAPI(
      graphqlOperation(getTaskById, {
        id: taskId,
      })
    );
  }

  static getCommentsByTaskId(taskId) {
    return promiseWrapperAPI(graphqlOperation(getCommentsByTaskId, { taskId }));
  }

  static searchTaskTypes(params) {
    return promiseWrapperAPI(graphqlOperation(searchTaskTypes, params));
  }

  static searchPicklist(params) {
    return promiseWrapperAPI(graphqlOperation(searchPicklists, params));
  }

  static searchTeams(params) {
    return promiseWrapperAPI(graphqlOperation(searchTeams, params));
  }

  static searchUsers(params) {
    return promiseWrapperAPI(graphqlOperation(searchUsers, params));
  }

  static updateSettings(key, settingsObject) {
    return promiseWrapperAPI(
      graphqlOperation(updateSettings, {
        input: {
          [key]: JSON.stringify(settingsObject),
        },
      })
    );
  }

  static getSettings(id) {
    return promiseWrapperAPI(graphqlOperation(getSettings, { id }));
  }

  static getAllEntityAttributes() {
    return promiseWrapperAPI(
      graphqlOperation(searchEntityAttributes, {
        limit: 10000,
      })
    );
  }

  static searchAssets(filter, sort, limit, nextToken) {
    const paramObj = {};
    if (filter) {
      paramObj.filter = filter;
    }
    if (sort) {
      paramObj.sort = sort;
    }
    if (limit) {
      paramObj.limit = limit;
    } else {
      paramObj.limit = 10000;
    }
    if (nextToken) {
      paramObj.nextToken = nextToken;
    }
    return promiseWrapperAPI(graphqlOperation(searchAssets, paramObj));
  }

  static getTasksByFieldPaginated(fieldId, startTimeLessThanDate, startTimeGreaterThanDate, size, nextToken) {
    const startTimeLessThan = (startTimeLessThanDate || moment().endOf('day').toDate()).toISOString();
    const startTimeGreaterThan = (startTimeGreaterThanDate || moment().startOf('day').toDate()).toISOString();

    const input = {
      fieldId,
      startTimeGreaterThan,
      startTimeLessThan,
    };
    if (size) {
      Object.assign(input, { size });
    }
    if (nextToken) {
      Object.assign(input, { nextToken });
    }

    return promiseWrapperAPI(graphqlOperation(getTasksByFieldPaginated, input));
  }

  static getTasksByAreasPaginated(areas, startTimeLessThanDate, startTimeGreaterThanDate, size, nextToken) {
    const startTimeLessThan = (startTimeLessThanDate || moment().startOf('day').toDate()).toISOString();
    const startTimeGreaterThan = (startTimeGreaterThanDate || moment().endOf('day').toDate()).toISOString();

    const input = {
      areas,
      startTimeGreaterThan,
      startTimeLessThan,
    };
    if (size) {
      Object.assign(input, { size });
    }
    if (nextToken) {
      Object.assign(input, { nextToken });
    }
    return promiseWrapperAPI(graphqlOperation(getTasksByAreasPaginated, input));
  }

  static async getRouteEngineHistoryForFieldId(fieldId, startDate, size, nextToken) {
    const input = {
      fieldId,
      startDate,
    };
    if (size) {
      Object.assign(input, { size });
    }
    if (nextToken) {
      Object.assign(input, { nextToken });
    }
    return promiseWrapperAPI(graphqlOperation(getRouteEngineHistoryByField, input));
  }

  static onUpdateRequestId(requestId) {
    return API.graphql(graphqlOperation(onUpdateRequestId, { requestId }));
  }

  static getEngineResult(requestId, pathToFile) {
    const input = {
      method: 'getEngineResult',
      data: JSON.stringify({ requestId, pathToFile }),
    };
    return this.rpcServiceMethod(input);
  }

  static uploadAssetsToUpdate(dataToUpdate, key) {
    const input = {
      method: 'uploadAssetsToUpdate',
      data: JSON.stringify({ dataToUpdate, key }),
    };
    return this.rpcServiceMethod(input);
  }

  static getEngineDebugData(requestId, pathToFile, tempData = false) {
    const input = {
      method: 'getEngineDebugData',
      data: JSON.stringify({ requestId, pathToFile, tempData }),
    };
    return this.rpcServiceMethod(input);
  }

  static async getStopDurationFromUserLocation(stopId, userId) {
    const input = {
      method: 'getStopDurationFromUserLocation',
      data: JSON.stringify({ stopId, userId }),
    };

    return this.rpcServiceMethod(input);
  }

  static getTaskTypeById(id) {
    return promiseWrapperAPI(graphqlOperation(getTaskType, { id }));
  }

  static getFactors(type = 'factor', tenantId, fieldId, size = 1000) {
    return promiseWrapperAPI(graphqlOperation(getFactorsByType, { type, tenantId, fieldId, size }));
  }

  static getFactorById(id) {
    return promiseWrapperAPI(graphqlOperation(getFactorById, { id }));
  }

  static deleteFactor(id) {
    return promiseWrapperAPI(
      graphqlOperation(deleteFactor, {
        input: { id },
      })
    );
  }
  static updateFactor(input) {
    return promiseWrapperAPI(graphqlOperation(updateFactor, { input }));
  }
  static createFactor(input) {
    return promiseWrapperAPI(graphqlOperation(createFactor, { input }));
  }

  static createFactor(input) {
    return promiseWrapperAPI(graphqlOperation(createFactor, { input }));
  }

  static createComment(createCommentInput) {
    return promiseWrapperAPI(graphqlOperation(createComment, { input: createCommentInput }));
  }

  static updateComment(updateCommentInput) {
    return promiseWrapperAPI(graphqlOperation(updateComment, { input: updateCommentInput }));
  }

  static onChangeFieldTask(subscriptionInput) {
    let subscriptionString = onChangeFieldTask;
    // vendorManagement Related changes to temporarily overcome the subscriptions issue with AWS SDK
    if (!subscriptionInput.vendorId) {
      subscriptionString = subscriptionString.replace(', $vendorId: String', '').replace(', vendorId: $vendorId', '');
      delete subscriptionInput.vendorId;
    }
    return API.graphql(graphqlOperation(subscriptionString, subscriptionInput));
  }

  static onUserLocationUpdate(subscriptionInput) {
    const subscriptionString = onUserLocationUpdate;
    return API.graphql(graphqlOperation(subscriptionString, subscriptionInput));
  }

  static onUpdateUser(subscriptionInput) {
    return API.graphql(graphqlOperation(onBulkUserChange, subscriptionInput));
  }

  static sendErrorLogToJoyn2Team({ message, appState, stackTrace, localStorage }) {
    const input = {
      method: 'sendErrorLogToJoyn2Team',
      data: JSON.stringify({ message, appState, stackTrace, localStorage }),
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static getAssigneeList(taskIds) {
    const input = {
      method: 'getAssigneeList',
      data: JSON.stringify({ taskIds }),
    };
    return this.rpcServiceMethod(input);
  }

  static getTaskTypesByAreas({ areaIds }) {
    const input = {
      method: 'getTaskTypesByAreas',
      data: JSON.stringify({ areaIds }),
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static bulkDownloadInitialData({ requests, fieldId, userId }) {
    const input = {
      method: 'bulkDownloadInitial',
      data: JSON.stringify({ enableBrotli: true, requests, fieldId, userId }),
    };
    return this.rpcServiceMethod(input, true);
  }

  static getScoreValue({ formula, assetId, taskTypeId, fieldId }) {
    const input = {
      method: 'getScoreValue',
      data: JSON.stringify({
        formula,
        assetId,
        taskTypeId,
        fieldId,
      }),
    };
    return this.rpcServiceMethod(input);
  }

  static getUserByAreas(ids) {
    return promiseWrapperAPI(
      graphqlOperation(getUserByAreas, {
        areas: ids,
        size: 10000,
      })
    );
  }

  static getEngineRunListPlannedAndActualRouteForUserAndDate(userId, date) {
    const input = {
      method: 'getEngineRunListPlannedAndActualRouteForUserAndDate',
      data: JSON.stringify({ userId, date }),
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static getFactorValue(formula, assetId) {
    const input = {
      method: 'getFactorValue',
      data: JSON.stringify({ formula, assetId }),
    };

    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static getExcludedFactors(taskTypeId, fieldId) {
    const input = {
      method: 'getExcludedFactors',
      data: JSON.stringify({ taskTypeId, fieldId }),
    };

    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static bulkDownloadTasks({
    areas,
    fieldId,
    fromDate,
    toDate,
    nextToken,
    approverId,
    requestorId,
    isApprovalWorkflow,
    approvalStatus,
    taskTypeGroupIDs = [],
    enableS3Upload = false,
  }) {
    const input = {
      method: 'bulkDownloadTasks',
      data: JSON.stringify({
        areas,
        fieldId,
        fromDate,
        toDate,
        nextToken,
        enableGzip: true,
        approverId,
        requestorId,
        isApprovalWorkflow,
        approvalStatus,
        taskTypeGroupIDs,
        enableS3Upload,
      }),
    };
    return this.rpcServiceMethod(input, false, true);
  }

  static getTasksByTaskIds(taskIds) {
    return this.getEntitiesByIds(taskIds, 'task');
  }

  static getPicklistByPicklistIds(picklistIds) {
    return this.getEntitiesByIds(picklistIds, 'picklist');
  }

  static getAssetsByIds(assetIds) {
    return this.getEntitiesByIds(assetIds, 'asset');
  }

  static getTaskTypesByIds(taskTypeIds) {
    return this.getEntitiesByIds(taskTypeIds, 'taskType');
  }

  static getFormsByFormIds(formdIds) {
    return this.getEntitiesByIds(formdIds, 'form');
  }

  static async getEntitiesByIds(ids, entityType) {
    const uniqueIds = Array.from(new Set(ids));
    const input = {
      method: 'getEntitiesByIds',
      data: JSON.stringify({ ids: uniqueIds, entityType, enableBrotli: true }),
    };
    const entities = await this.rpcServiceMethod(input, true);
    return entities.filter((e) => !e.isDeleted);
  }

  static getAssetsByTeamIdsAndAreaIds(teamIds, areaIds, searchString, types, stopId, size, id, filterAssetLevels) {
    const input = {
      method: 'getAssetsByTeamIdsAndAreaIds',
      data: JSON.stringify({
        teamIds,
        areaIds,
        searchString,
        types,
        stopId,
        size: size || (searchString ? 100 : undefined),
        id,
        filterAssetLevels,
      }),
    };
    return this.rpcServiceMethod(input);
  }

  static getAssetsByAssetsCriteria({
    areaIds = [],
    types = [],
    customAttributes = {},
    stopIds = [],
    fieldId,
    assetIds = [],
  }) {
    const input = {
      method: 'getAssetsByAssetsCriteria',
      data: JSON.stringify({
        assetCriteria: {
          areaIds,
          types,
          customAttributes,
          stopIds,
          fieldId,
          assetIds,
        },
        projections: ['name', 'type'],
      }),
    };
    return this.rpcServiceMethod(input);
  }

  static getUsersByTeamIdsAndAreaIds(teamIds, areaIds, fieldId) {
    const input = {
      method: 'getUsersByTeamIdsAndAreaIds',
      data: JSON.stringify({ teamIds, areaIds, fieldId }),
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static getLogsListOfAUser(user, logType) {
    const input = {
      method: 'getLogsListOfAUser',
      data: JSON.stringify({ logType, user }),
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static getFileObject(key, logs) {
    const input = {
      method: 'getFileObject',
      data: JSON.stringify({ key, logs }),
    };
    return this.rpcServiceMethod(input, false, true);
  }

  static getPlannedRouteForParticularRun(userId, date, runId) {
    const input = {
      method: 'getPlannedRoueForParticularRun',
      data: JSON.stringify({ userId, date, runId }),
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static getUsersLocationByField(fieldId) {
    const input = {
      method: 'getUsersLocationByField',
      data: JSON.stringify({ fieldId }),
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static getGeofenceEventsByUser(userId, date) {
    const input = {
      method: 'getGeofenceEventsByUser',
      data: JSON.stringify({ userId, date }),
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static getLastLocationAndEventInfoForUser(userId) {
    const input = {
      method: 'getLastLocationAndEventInfoForUser',
      data: JSON.stringify({ userId }),
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static getRoles() {
    return promiseWrapperAPI(graphqlOperation(getRoles));
  }

  static deleteRole(roleId) {
    return promiseWrapperAPI(
      graphqlOperation(deleteRole, {
        input: {
          id: roleId,
        },
      })
    );
  }

  static updateRole(role) {
    return promiseWrapperAPI(graphqlOperation(updateRole, { input: role }));
  }

  static createRole(role) {
    return promiseWrapperAPI(graphqlOperation(createRole, { input: role }));
  }

  static getChildTasksByParentTaskId(parentTaskId) {
    const input = {
      method: 'getChildTasksByParentTaskId',
      data: JSON.stringify({ parentTaskId }),
    };
    return this.rpcServiceMethod(input, false);
  }

  static confirmTasks(taskId) {
    const input = {
      method: 'confirmTasks',
      data: JSON.stringify({ taskId }),
    };
    return this.rpcServiceMethod(input, false);
  }

  static async bulkDownloadPicklists() {
    const input = {
      method: 'bulkDownloadPicklists',
      data: '{}',
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static async upsertPicklist(picklist) {
    const input = {
      method: 'upsertPicklist',
      data: JSON.stringify(picklist),
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static async getAllFormSchemasByField(fieldId) {
    const formSchemas = await this.bulkDownloadForms({ type: 'formschema', fieldId });
    return formSchemas;
  }

  static async bulkDownloadForms(queryParams) {
    const input = {
      method: 'bulkDownloadForms',
      data: JSON.stringify({ ...queryParams, enableBrotli: true }),
    };
    return this.rpcServiceMethod(input, true);
  }

  static async upsertForm(form) {
    const input = {
      method: 'upsertForm',
      data: JSON.stringify(form),
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static async bulkDownloadApprovalMatrices() {
    const input = {
      method: 'bulkDownloadApprovalMatrices',
      data: '{}',
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static async upsertApprovalMatrix(approvalMatrix) {
    const input = {
      method: 'upsertApprovalMatrix',
      data: JSON.stringify(approvalMatrix),
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static async bulkDownloadContacts() {
    const input = {
      method: 'genericTableAction',
      data: '{}',
      subMethod: 'getAllContacts',
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static async bulkDownloadCustomReports() {
    const input = {
      method: 'genericTableAction',
      data: '{}',
      subMethod: 'getAllCustomReports',
    };
    return this.rpcServiceMethod(input);
  }

  static async getReportData(data) {
    const input = {
      method: 'getReportData',
      data: JSON.stringify(data),
    };
    return this.rpcServiceMethod(input);
  }

  static async upsertContact(contact) {
    const input = {
      method: 'genericTableAction',
      data: JSON.stringify(contact),
      subMethod: 'upsertContact',
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static async upsertConfigForUser(userConfig) {
    const input = {
      method: 'genericTableAction',
      data: JSON.stringify(userConfig),
      subMethod: 'upsertConfigForUser',
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static async getContactsByContactIds(contactIds) {
    return this.getEntitiesByIds(contactIds, 'contact');
  }

  static async getAllInstructions() {
    const input = {
      method: 'genericTableAction',
      data: '{}',
      subMethod: 'getAllInstructions',
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static async upsertInstruction(instruction) {
    const input = {
      method: 'genericTableAction',
      data: JSON.stringify(instruction),
      subMethod: 'upsertInstruction',
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static async getAllVendors() {
    const input = {
      method: 'genericTableAction',
      data: '{}',
      subMethod: 'getAllVendors',
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static async getAllTriggerApi() {
    const input = {
      method: 'genericTableAction',
      data: '{}',
      subMethod: 'getAllTriggerApis',
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static async upsertVendor(vendor) {
    const input = {
      method: 'genericTableAction',
      data: JSON.stringify(vendor),
      subMethod: 'upsertVendor',
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static async getAllAttachments() {
    const input = {
      method: 'genericTableAction',
      data: '{}',
      subMethod: 'getAllAttachments',
    };
    return this.rpcServiceMethod(input);
  }

  static async upsertAttachment(attachment) {
    const input = {
      method: 'genericTableAction',
      subMethod: 'upsertAttachment',
      data: JSON.stringify(attachment),
    };
    return this.rpcServiceMethod(input);
  }

  static async bulkDownloadEngineSchedules() {
    const input = {
      method: 'genericTableAction',
      data: '{}',
      subMethod: 'getAllEngineSchedules',
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static async upsertEngineSchedule(schedule) {
    const input = {
      method: 'genericTableAction',
      subMethod: 'upsertEngineSchedule',
      data: JSON.stringify(schedule),
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static upsertFilterApi(data) {
    const input = {
      method: 'genericTableAction',
      subMethod: 'upsertFilter',
      data: JSON.stringify(data),
    };
    return this.rpcServiceMethod(input);
  }

  static getAllSavedFiltersOfAUser(userId) {
    const input = {
      method: 'genericTableAction',
      subMethod: 'getFiltersForUser',
      data: JSON.stringify({ userId }),
    };
    return this.rpcServiceMethod(input);
  }

  static deleteFilterApi(data) {
    const input = {
      method: 'genericTableAction',
      subMethod: 'deleteFilter',
      data: JSON.stringify(data),
    };
    return this.rpcServiceMethod(input);
  }

  static async getAttributesOfObjectType(type) {
    const input = {
      method: 'getAttributesOfObjectType',
      data: JSON.stringify(type),
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static onCommentCreated(fieldId, tenantId) {
    return API.graphql(graphqlOperation(onCreateComment, { fieldId, tenantId }));
  }

  static onCommentUpdated(fieldId, tenantId) {
    return API.graphql(graphqlOperation(onUpdateComment, { fieldId, tenantId }));
  }

  static onCommentDeleted(fieldId, tenantId) {
    return API.graphql(graphqlOperation(onDeleteComment, { fieldId, tenantId }));
  }

  static async getAuditLogOfForm(formDataId) {
    const input = {
      method: 'getAuditLogForForm',
      data: JSON.stringify({ id: formDataId }),
    };
    const response = await this.rpcServiceMethod(input);
    return response;
  }

  static updateSignature(formData) {
    const input = {
      method: 'updateSignature',
      data: JSON.stringify(formData),
    };
    return this.rpcServiceMethod(input);
  }

  static bulkDownloadEntityAttributes(queryParams) {
    const input = {
      method: 'bulkDownloadEntityAttributes',
      data: JSON.stringify(queryParams),
    };
    return this.rpcServiceMethod(input);
  }

  static async upsertEntityAttribute(attribute) {
    const input = {
      method: 'upsertEntityAttributeApi',
      data: JSON.stringify(attribute),
    };
    return this.rpcServiceMethod(input);
  }

  static async approveTask(data) {
    const input = {
      method: 'approveTask',
      data: JSON.stringify(data),
    };
    return this.rpcServiceMethod(input);
  }

  static async upsertTask(task) {
    const input = {
      method: 'upsertTask',
      data: JSON.stringify(task),
    };
    return this.rpcServiceMethod(input);
  }

  static async bulkApprove(data) {
    const input = {
      method: 'bulkApprove',
      data: JSON.stringify(data),
    };
    return this.rpcServiceMethod(input);
  }

  static async checkIfTaskTypeCodeExits(data) {
    const input = {
      method: 'checkIfTaskTypeCodeExits',
      data: JSON.stringify(data),
    };
    return this.rpcServiceMethod(input);
  }

  static async generateMultipleTasksApi(data) {
    const input = {
      method: 'generateMultipleTasks',
      data: JSON.stringify(data),
    };
    return this.rpcServiceMethod(input);
  }

  static async downloadS3Object(uploadUrl) {
    const response = await fetch(uploadUrl, {
      method: 'GET',
    });
    if (response.status.toString().match(/^2\d{2}/)) {
      return response;
    }
    console.error(response);
    throw new Error(response.statusText);
  }

  static async rpcServiceMethod(input, brotliDecode = false, zlibInflate = false) {
    const response = await promiseWrapperAPI(graphqlOperation(rpcService, { input }));
    if (!response) throw new Error('Response is null');
    const success = _.get(response, 'data.rpcService.success');
    let message = _.get(response, 'data.rpcService.message');
    if (!success) {
      if (!message && _.size(response.errors)) {
        const messageArr = response.errors.map((error) => error.message);
        message = (messageArr || []).join(', ');
      }
      throw new Error(message);
    }
    let output = _.get(response, 'data.rpcService.output');
    if (output) output = JSON.parse(output);
    if (output && output.url) {
      const res = await this.downloadS3Object(output.url);

      const string = await res.text();

      output = string;
    }
    if (brotliDecode && output) {
      output = BrotliDecodeFromBase64String.decodeArrayBuffer(output);
    } else if (zlibInflate && output) {
      output = zlibDeflate(output);
    }
    return output || message;
  }

  static getTaskScoreForTask(task) {
    const input = {
      method: 'getTaskScoreForTask',
      data: JSON.stringify({ task }),
    };
    return this.rpcServiceMethod(input);
  }

  static async getUploadUrlForFileName(fileName) {
    // key can be string or if object then {name,id}
    if (Array.isArray(fileName) && fileName?.length) {
      const response = await this.getUploadUrlsForFileNames(fileName);
      const uploadUrlObjs = _.get(response, 'output.processedFileNames');
      if (!uploadUrlObjs?.length) {
        const unprocessedObjs = _.get(response, 'output.unprocessedFileNames');
        if (!unprocessedObjs?.length) {
          throw new Error('Did not receive response from integration api');
        }
        throw new Error(unprocessedObjs[0].errorMessage);
      }
      return uploadUrlObjs;
    }
    const response = await this.getUploadUrlsForFileNames([fileName]);
    const uploadUrlObj = _.get(response, 'output.processedFileNames[0]');
    if (!uploadUrlObj) {
      const unprocessedObj = _.get(response, 'output.unprocessedFileNames[0]');
      if (!unprocessedObj) {
        throw new Error('Did not receive response from integration api');
      }
      throw new Error(unprocessedObj.errorMessage);
    }
    return uploadUrlObj;
  }

  static async getUploadUrlsForFileNames(fileNames) {
    const response = await API.post(INTEGRATION_API_NAME, '/api/uploaddocument', { body: fileNames });
    if (!response.success) {
      throw new Error(response.message);
    }
    return response;
  }

  static async upsertAsset(assets) {
    const response = await API.post(INTEGRATION_API_NAME, '/api/asset/bulk', { body: assets });
    if (!response.success) {
      throw new Error(response.message);
    }
    return response;
  }

  static async getDownloadUrlForS3Key(key) {
    // key can be string or if object then {s3Key,expires}
    const response = await this.getDownloadUrlsForS3Keys([key]);
    const downloadUrlObj = _.get(response, 'output.processedKeys[0]');
    if (!downloadUrlObj) {
      const unprocessedObj = _.get(response, 'output.unprocessedKeys[0]');
      if (!unprocessedObj) {
        throw new Error('Did not receive response from integration api');
      }
      throw new Error(unprocessedObj.errorMessage);
    }
    return downloadUrlObj.downloadUrl;
  }

  static async getDownloadUrlsForS3Keys(keys) {
    const response = await API.post(INTEGRATION_API_NAME, '/api/downloaddocument', { body: keys });
    if (!response.success) {
      throw new Error(response.message);
    }
    return response;
  }

  // taskId = "4f3db6ed-177e-4aad-8781-fdd8febebe2b",
  // assetId = "c2cb83bd-9c9a-4f1c-a3d4-1d826219f82f",

  // taskId = '1bba6fa3-7aeb-47c4-8879-d48865f690bc',
  //     assetId = '576e4f77-01ae-46e5-a5dc-0a4f57180373',
  static async getReadingData({ taskId, assetId }) {
    const response = await API.get(
      FDG_API_NAME,
      `/admin/api/ReadingPivotGrid/GetReadingGrid?readinguuid=${taskId}&assetuuid=${assetId}`
    );
    if (!response.success) {
      throw new Error(response.message);
    }
    return response;
  }

  static async uploadFile(uploadUrl, fileType, bodyStream) {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': fileType,
      },
      body: bodyStream,
    });
    if (response.status.toString().match(/^2\d{2}/)) {
      return response;
    }
    console.error(response);
    throw new Error(response.statusText);
  }

  static refreshMetadataPicklist(picklistId) {
    const input = {
      method: 'refreshMetadataPicklist',
      data: JSON.stringify({ id: picklistId }),
    };
    return this.rpcServiceMethod(input);
  }

  static async upsertPicklistMapping(picklistMapping) {
    const input = {
      method: 'genericTableAction',
      subMethod: 'upsertPicklistMapping',
      data: JSON.stringify(picklistMapping),
    };
    return this.rpcServiceMethod(input);
  }

  static async getAllPicklistMappings(data) {
    const input = {
      method: 'genericTableAction',
      data: JSON.stringify(data) || '{}',
      subMethod: 'getAllPicklistMappings',
    };
    return this.rpcServiceMethod(input);
  }

  static async getAllEvalutedRulesOfATask(data) {
    const input = {
      method: 'genericTableAction',
      data: JSON.stringify(data) || '{}',
      subMethod: 'getAllEvalutedRulesOfATask',
    };
    return this.rpcServiceMethod(input);
  }

  static async acceptTask(data) {
    const input = {
      method: 'acceptMultiAssignedTask',
      data: JSON.stringify(data),
    };
    return this.rpcServiceMethod(input);
  }

  static async rejectTask(data) {
    const input = {
      method: 'rejectTask',
      data: JSON.stringify(data),
    };
    return this.rpcServiceMethod(input);
  }

  static async getAssetsByCustomFilters(data) {
    const input = {
      method: 'getAssetsByCustomFilters',
      data: JSON.stringify(data),
    };
    return this.rpcServiceMethod(input);
  }

  static async exportAllAssets(data) {
    const input = {
      method: 'exportAllAssets',
      data: JSON.stringify(data),
    };
    return this.rpcServiceMethod(input);
  }

  static async exportAllUsers(data) {
    console.log(data);
    const input = {
      method: 'exportAllUsers',
      data: JSON.stringify(data),
    };
    return this.rpcServiceMethod(input);
  }

  static async bulkExportTaskData(data) {
    const input = {
      method: 'bulkExportTaskData',
      data: JSON.stringify(data),
    };
    return this.rpcServiceMethod(input, true);
  }

  static async getUsersByCustomFilters(data) {
    const input = {
      method: 'getUsersByCustomFilters',
      data: JSON.stringify(data),
    };
    return this.rpcServiceMethod(input);
  }

  static async getAssetsByAreaIdsAndSearchString(data) {
    const input = {
      method: 'getAssetsByAreaIdsAndSearchString',
      data: JSON.stringify(data),
    };
    return this.rpcServiceMethod(input);
  }

  static async getTaskTypesByFieldsAndSearchString(data) {
    const input = {
      method: 'getTaskTypesByFieldsAndSearchString',
      data: JSON.stringify(data),
    };
    return this.rpcServiceMethod(input);
  }

  static async getStopsBySearchStringAndFieldids(data) {
    const input = {
      method: 'getStopsBySearchStringAndFieldids',
      data: JSON.stringify(data),
    };
    return this.rpcServiceMethod(input);
  }

  static async getTaskEmailHistory(data) {
    const input = {
      method: 'getTaskEmailHistory',
      data: JSON.stringify(data),
    };
    return this.rpcServiceMethod(input);
  }

  static async bulkDownloadWatcherGroups(fieldId) {
    const input = {
      method: 'genericTableAction',
      data: JSON.stringify({ fieldId }),
      subMethod: 'getAllWatcherGroups',
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static async upsertWatchergroup(watchergroup) {
    const input = {
      method: 'genericTableAction',
      data: JSON.stringify(watchergroup),
      subMethod: 'upsertWatchergroup',
    };
    return promiseWrapperAPI(graphqlOperation(rpcService, { input }));
  }

  static async getActiveCountTasksByTaskTypeIds(taskTypeIds) {
    const input = {
      method: 'getActiveTasksCountByTaskTypeIds',
      data: JSON.stringify({ taskTypeIds }),
    };
    return this.rpcServiceMethod(input);
  }

  static async bulkDownloadRulesForATaskType(taskTypeId) {
    const input = {
      method: 'genericTableAction',
      data: JSON.stringify({ taskTypeId }),
      subMethod: 'getRulesForTaskType',
    };
    return this.rpcServiceMethod(input);
  }

  static async bulkUpsertTaskTypeRules(rules) {
    const input = {
      method: 'bulkGenericTableAction',
      data: JSON.stringify(rules),
      subMethod: 'upsertTaskTypeRule',
    };
    return this.rpcServiceMethod(input);
  }

  static async deleteTaskTypeRule(data) {
    const input = {
      method: 'genericTableAction',
      data: JSON.stringify(data),
      subMethod: 'deleteTaskTypeRule',
    };
    return this.rpcServiceMethod(input);
  }

  static async loadAnalyticsData(data) {
    const input = {
      method: 'fetchAnalyticsData',
      data: JSON.stringify(data),
    };
    return this.rpcServiceMethod(input);
  }

  static async forgotPassword(body) {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    };
    let response = await fetch(`${awsmobile.API.endpoints[0].endpoint}/api/forgotpassword`, requestOptions);
    response = await response.json();
    if (!response.success) {
      throw new Error(response.message);
    }
    return response?.output ?? {};
  }

  static async getAssetLevelsInfo({ assetId }) {
    const input = {
      method: 'getAssetLevelsInfo',
      data: JSON.stringify({ assetId }),
    };
    return this.rpcServiceMethod(input, true, false);
  }

  static async getTasksWatchedByMe(data) {
    const input = {
      method: 'getTasksWatchedByMe',
      data: JSON.stringify({ ...data, enableGzip: true }),
    };
    return this.rpcServiceMethod(input, false, true);
  }

  static async generateUrlForQr(data) {
    const input = {
      method: 'generateUrlForQr',
      data: JSON.stringify(data),
    };
    return this.rpcServiceMethod(input, false, false);
  }

  static async getAutologinCredential(body) {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    };
    let response = await fetch(`${awsmobile.API.endpoints[0].endpoint}/api/getautologincredential`, requestOptions);
    response = await response.json();
    if (!response.success) {
      throw new Error(response.message);
    }
    const output = BrotliDecodeFromBase64String.decodeArrayBuffer(response?.output ?? {});
    return output;
  }
}

export default ApiService;
